/**
 * Screenshots for an issue report.
 *
 * The report dialog captures the page the reporter is looking at the moment it
 * opens (`captureScreenshot`) and attaches it — a reporter should not have to
 * know their operating system's screenshot key for the team to see what they
 * saw. The capture uses `html2canvas-pro` (html2canvas with modern CSS colour
 * support), loaded LAZILY so the library costs nothing until somebody reports.
 *
 * What is NOT captured:
 * - the Support panels themselves (anything inside `[data-support-ui]`);
 * - the VALUE of password fields and of any field or element marked
 *   `data-sensitive` (blanked in the clone, never in the live page) — a
 *   screenshot travels to the support team and its AI, and a password in it
 *   would be the worst thing a bug report could leak.
 *
 * A capture that fails (a cross-origin canvas, a very large page) resolves to
 * null and the dialog offers "Attach a screenshot" (a file picker) instead.
 */

/** Whether this build can capture the page itself. */
export const SCREENSHOT_CAPTURE_SUPPORTED = typeof window !== 'undefined'

const MAX_EDGE = 2200

function blankSensitive(doc: Document): void {
  doc.querySelectorAll<HTMLInputElement>('input[type="password"], [data-sensitive] input, input[data-sensitive]')
    .forEach((el) => { el.value = ''; el.setAttribute('value', '') })
  doc.querySelectorAll<HTMLElement>('[data-sensitive]').forEach((el) => {
    if (!(el instanceof HTMLInputElement)) el.textContent = '••••'
  })
}

export interface CaptureOptions {
  /** The whole scroll height, not just the viewport (a test's failure shot). */
  fullPage?: boolean
  /** Output format; PNG unless told otherwise. */
  type?: 'png' | 'jpeg'
  /** 0–1, for JPEG. */
  quality?: number
}

/** Capture the visible page (or `target`) as a PNG blob, or null.
 *  Also the screenshot of a browser-driven acceptance run's every step
 *  (frontend/src/testing/browserTestBoot.ts), which asks for JPEG and, on a
 *  failure, the full page. */
export async function captureScreenshot(target?: HTMLElement | null,
  options: CaptureOptions = {}): Promise<Blob | null> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null
  try {
    const { default: html2canvas } = await import('html2canvas-pro')
    const el = target || document.body
    const full = !!options.fullPage
    const docEl = document.documentElement
    const width = full ? Math.max(docEl.scrollWidth, window.innerWidth) : window.innerWidth
    const height = full ? Math.max(docEl.scrollHeight, window.innerHeight) : window.innerHeight
    const scale = Math.min(window.devicePixelRatio || 1, MAX_EDGE / Math.max(width, height, 1))
    const canvas = await html2canvas(el, {
      // The viewport the reporter sees, not the whole scroll height — unless
      // the whole page was asked for.
      x: full ? 0 : window.scrollX, y: full ? 0 : window.scrollY,
      width, height,
      windowWidth: docEl.clientWidth,
      windowHeight: full ? height : docEl.clientHeight,
      scale: Math.max(full ? 0.25 : 0.5, scale),
      useCORS: true,
      logging: false,
      ignoreElements: (node: Element) => !!(node as HTMLElement).closest?.('[data-support-ui]'),
      onclone: (doc: Document) => blankSensitive(doc),
    })
    const mime = options.type === 'jpeg' ? 'image/jpeg' : 'image/png'
    const quality = typeof options.quality === 'number' ? options.quality : 0.92
    return await new Promise<Blob | null>((resolve) => {
      try { canvas.toBlob((b) => resolve(b), mime, quality) } catch { resolve(null) }
    })
  } catch {
    // Diagnostics only: a failed capture must never block a report.
    return null
  }
}

/** The capture as a File ready to attach. */
export async function captureScreenshotFile(target?: HTMLElement | null): Promise<File | null> {
  const blob = await captureScreenshot(target)
  if (!blob) return null
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
  try {
    return new File([blob], `screen-${stamp}.png`, { type: 'image/png' })
  } catch {
    return null
  }
}

/** The attachment types the server accepts (`store.ALLOWED_ATTACHMENT_MIME`). */
export const ATTACHMENT_ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv,application/json,video/mp4,video/webm'

/** The server's per-file ceiling (`store.MAX_ATTACHMENT_BYTES`). */
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024

const ACCEPTED = new Set(ATTACHMENT_ACCEPT.split(','))

/** Why a file cannot be attached, or '' when it can. Checked before upload so
 *  the reporter hears about it while the dialog is still open. */
export function attachmentProblem(file: File): string {
  if (!file.size) return `${file.name} is empty.`
  if (file.size > MAX_ATTACHMENT_BYTES) return `${file.name} is larger than 15 MB.`
  const mime = (file.type || '').toLowerCase()
  if (!ACCEPTED.has(mime) && !(mime === 'image/jpg')) {
    return `${file.name}: files of this type cannot be attached (images, PDF, text, CSV, JSON or video only).`
  }
  return ''
}
